import React, { useContext } from 'react'
import type { BaseRange } from 'slate'
import { Editor, Element, Range, Text, Transforms } from 'slate'
import { ReactEditor, useSlateStatic } from 'slate-react'
import type { RefObject } from 'react'
import { useSelector } from 'react-redux'
import cc from 'classcat'

import { Popover } from '../../../../../Popover'
import { ToolbarContext } from '../RichTextEditor'
import { isAnyButtonSelected } from '../buttons'
import { themeGroupDelimiter } from '../../../../../../../utils/themes'
import { useColorOptions } from '../../../../../../utils/hooks/colorSettings'
import { useTranslate } from '../../../../../../utils/translate'
import ColorpickerField from '../../../../../formComponents/ColorpickerField'

function ColorButton({
  editableRef: _onlyUsedToForceARerender,
}: {
  readonly editableRef?: RefObject<HTMLElement | null>
}) {
  const t = useTranslate('interface')
  const [{ isColorPickerOpen: isOpen, isButtonLinkTooltipOpen }, toolbarDispatch] = useContext(ToolbarContext)

  const themeName = useSelector<State, string>((state) => state.getIn(['shop', 'theme', 'name'], '').replace('.', '_'))
  const [themeGroupName] = themeName.split(themeGroupDelimiter)
  const tThemeSettings = (subKey: string, ...args: any[]) => t(`themes:${themeGroupName}.settings.${subKey}`, ...args)
  const tColorOptions = (subKey: string, ...args: any[]) =>
    t(`components.colorFieldComponent.popover.colorPresets.${subKey}.tooltip`, ...args)
  const colorOptions = useColorOptions(tColorOptions, tThemeSettings)

  const editor = useSlateStatic()
  const lastSelection: RefObject<BaseRange | undefined> = editor.useLastSelection(editor)

  const isButtonSelected = isAnyButtonSelected(editor) || isButtonLinkTooltipOpen

  const ref = React.useRef<HTMLButtonElement>(null)

  const marks = Editor.marks(editor)
  const currentColor = marks
    ? marks.color
    : Array.from(Editor.nodes(editor, { at: lastSelection.current, match: Text.isText }))[0]?.[0].color

  const matchListItem = (n: Element) => n.type === 'list-item'

  const matchListItemChild = (n: Element) => n.type === 'list-item-child'

  const setListItemNodes = (color: string | undefined, isLastSelection?: boolean) =>
    Transforms.setNodes(
      editor,
      { color },
      {
        mode: 'all',
        match: matchListItem,
        ...(isLastSelection && { at: lastSelection.current }),
      },
    )

  const setListMarkersColor = (color?: string, isLastSelection?: boolean) => {
    const listItemChildNodes = Array.from(
      Editor.nodes(editor, { match: matchListItemChild, ...(isLastSelection && { at: lastSelection.current }) }),
    )

    const listItemChildNode = listItemChildNodes[0]?.[0]

    if (!listItemChildNode || !Element.isElement(listItemChildNode)) {
      return
    }

    if (!color) {
      setListItemNodes(undefined, isLastSelection)
    }

    const firstChild = listItemChildNode.children[0]
    const firstChildrenColor = firstChild && 'color' in firstChild && (firstChild.color || null)
    const isEveryChildWTheSameColor = listItemChildNode.children.every(
      (childNode: any) => childNode.color === firstChildrenColor,
    )

    if (isEveryChildWTheSameColor) {
      setListItemNodes(color, isLastSelection)
    }
  }

  const handleChange = (color: string) => {
    if (editor.selection) {
      Editor.addMark(editor, 'color', color)
      setListMarkersColor(color)
    } else if (lastSelection.current && Range.isExpanded(lastSelection.current)) {
      Transforms.setNodes(editor, { color }, { match: Text.isText, split: true, at: lastSelection.current })
      setListMarkersColor(color, true)
    } else {
      editor.marks = { ...(marks || {}), color }
      editor.onChange()
    }
  }

  const handleChangeComplete = () => {
    editor.onChange()
    ReactEditor.focus(editor)

    if (!editor.selection && lastSelection.current && editor.isValidRange(editor, lastSelection.current)) {
      Transforms.select(editor, lastSelection.current)
    }
  }

  const handleReset = () => {
    Editor.removeMark(editor, 'color')
    setListMarkersColor()
    if (!editor.selection && lastSelection.current) Transforms.select(editor, lastSelection.current)
    ReactEditor.focus(editor)
  }

  return (
    <>
      <button
        ref={ref}
        className={cc([
          'dali-plugin-text-toolbar-button',
          'dali-plugin-text-toolbar-button-color',
          { active: Boolean(currentColor) },
        ])}
        data-command-name="color"
        type="button"
        title={t('components.richTextComponent.colorButton.tooltip')}
        onClick={() => {
          toolbarDispatch({ type: 'toggle_color_picker' })
          if (!editor.selection && lastSelection.current) Transforms.select(editor, lastSelection.current)
        }}
        disabled={isButtonSelected}
      />
      <Popover
        open={isOpen}
        virtualRef={ref}
        onEscapeKeyDown={() => {
          toolbarDispatch({ type: 'toggle_color_picker' })
        }}
        variant="dark"
        avoidCollisions
      >
        <div>
          <ColorpickerField
            className="colorpicker"
            dimensions={{ width: 220, height: 200, hueWidth: 14, spacing: 7 }}
            value={currentColor || '#000000'}
            onChange={handleChange}
            onChangeComplete={handleChangeComplete}
            presetColors={colorOptions.toJS()}
          />
          <button type="button" className="ep-color-settings-reset-button" onClick={handleReset}>
            {t('components.colorFieldComponent.popover.resetButton.label')}
          </button>
        </div>
      </Popover>
    </>
  )
}

export default ColorButton
